import { PACKAGES } from "@/constants/testIds";
import { Check } from "lucide-react";

const PACKAGE_LIST = [
  {
    key: "petite",
    name: "Petite Play",
    glyph: "Petite",
    badge: "Most intimate",
    price: 225,
    tag: "Ideal for intimate spaces & first birthdays",
    image: "/images/softplay-circus.jpg",
    imagePosition: "object-bottom",
    features: [
      "5×5 Ball Pit",
      "Soft Play Tunnel & Moon Rocker",
      "1 Animal Hopper",
      "3 Foam Blocks",
      "Soft Flooring Mats & White Safety Fence",
    ],
    accent: "bg-brand-blush",
    cardTestId: PACKAGES.cardPetite,
    bookTestId: PACKAGES.bookPetite,
    bookLabel: "Book Petite",
  },
  {
    key: "signature",
    name: "Signature Play",
    glyph: "Signature",
    badge: "Most popular",
    price: 300,
    tag: "The complete setup for maximum fun",
    image: "/images/backyard-bunny.webp",
    imagePosition: "object-bottom",
    features: [
      "Everything in Petite",
      "Pyramid Climber & Slide",
      "Ride-on Car",
      "+1 Animal Hopper (2 total)",
      "6 Foam Blocks",
    ],
    accent: "bg-brand-sage",
    cardTestId: PACKAGES.cardSignature,
    bookTestId: PACKAGES.bookSignature,
    bookLabel: "Book Signature",
    featured: true,
  },
  {
    key: "royal",
    name: "Royal Bounce Experience",
    glyph: "Royal",
    badge: "The ultimate",
    price: 400,
    tag: "Elevate your decor with a White Castle Bounce",
    image: "/images/backyard-chris.webp",
    imagePosition: "object-bottom",
    features: [
      "Everything in Signature",
      "White Castle Bounce House",
      "Premium photo-ready setup",
      "Coordinated neutral palette",
    ],
    accent: "bg-brand-gold/60",
    cardTestId: PACKAGES.cardRoyal,
    bookTestId: PACKAGES.bookRoyal,
    bookLabel: "Book Royal",
  },
];

export default function Packages({ onBook }) {
  return (
    <section
      id="packages"
      data-testid={PACKAGES.section}
      className="relative py-24 md:py-32 bg-brand-base overflow-hidden"
    >
      {/* soft color field for the glass to refract */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-[12%] w-[420px] h-[420px] rounded-full bg-brand-sage/50 blur-3xl" />
        <div className="absolute top-1/3 -right-28 w-[400px] h-[400px] rounded-full bg-brand-blush/50 blur-3xl" />
        <div className="absolute -bottom-32 left-[38%] w-[380px] h-[380px] rounded-full bg-brand-gold/25 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 md:items-end mb-16">
          <div className="md:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-terracotta mb-5">
              Play Packages
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-brand-ink">
              Three curated experiences,
              <br />
              <span className="italic text-brand-terracotta">all sanitized</span> before every event.
            </h2>
          </div>
          <p className="md:col-span-5 text-base md:text-lg text-brand-mute font-light leading-relaxed">
            Every package includes delivery, setup, takedown and full sanitization.
            Pick the size that fits your space — we handle the rest.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {PACKAGE_LIST.map((p) => (
            <article
              key={p.key}
              data-testid={p.cardTestId}
              className={`relative flex flex-col rounded-3xl overflow-hidden glass-card transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                p.featured ? "glass-card-accent" : ""
              }`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className={`w-full h-full object-cover ${p.imagePosition || "object-center"} transition-transform duration-700 hover:scale-105`}
                />
                <span
                  className={`absolute top-4 left-4 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-ink ${p.accent}`}
                >
                  {p.badge}
                </span>
              </div>

              <div className="p-8 flex-1 flex flex-col">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-terracotta">
                  {p.glyph}
                </p>
                <h3 className="font-serif text-3xl md:text-[34px] font-normal text-brand-ink mt-2 leading-tight">
                  {p.name}
                </h3>
                <p className="mt-3 text-brand-mute text-sm leading-relaxed">{p.tag}</p>

                <div className="my-7 flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-taupe">
                    Starting at
                  </span>
                  <span className="font-serif text-4xl text-brand-ink">${p.price}</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-brand-mute">
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-sage/70 text-brand-ink">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="text-[11px] uppercase tracking-[0.2em] text-brand-taupe mb-5">
                  Delivery · Setup · Takedown · Sanitization
                </div>

                <button
                  data-testid={p.bookTestId}
                  onClick={() => onBook(p.name)}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 ${
                    p.featured ? "glass-btn-solid" : "glass-btn"
                  }`}
                >
                  {p.bookLabel}
                  <span aria-hidden>→</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export { PACKAGE_LIST };
